import { useEffect, useState, type ReactNode } from "react";
import type { GroupInfo, MaterialKind, MaterialPresetItem } from "../../../../shared/api-contract";
import { useViewer } from "../../../../state/ViewerProvider";
import { notice, useDomainState } from "../../../../state/editor/useEditor";
import { Icon } from "../../../kit";
import { MaterialCategoryGrid, type LibMaterial } from "../../library/MaterialCategoryGrid";
import { BASIC_CONTROLS, type SwatchDef } from "../macros";
import type { AppliedStep, BasicEngine, PresetMark } from "../applyEngine";
import { fixPartKind } from "../kindFix";
import { useBasicEngine } from "../useBasicEngine";
import { Chip, Note, Section, Swatch, Tile } from "./ui";
import { EVERY_PART, partName, usePartScope } from "./usePartScope";

/** One panel's part picker — created ONCE per panel and shared by its grid,
 *  its finish/tint rows and its chips, so they cannot disagree on the parts. */
type PartScope = ReturnType<typeof usePartScope>;

/**
 * Basic's five material panels — Metals · Gems · Ceramics · Pearls · Other —
 * the reference's material presets, as library grids with no selection gate.
 *
 * Each panel picks its parts with "Apply to" chips and applies through the
 * Basic engine (`applyLibraryMaterial`), so a click is ONE Undo step that puts
 * back the material and every look value it replaced. The grid itself is the
 * inspector's MaterialCategoryGrid, given a `target`; the RPC each tile needs
 * is chosen in the engine (`putPreset`).
 */

type Category = MaterialPresetItem["category"];

const fail = (e: Error) => notice(`Couldn't apply that: ${e.message}`);
const run = (p: Promise<AppliedStep | null>) => void p.catch(fail);

/** The words an Undo step and the status line use, per category. */
const STEP_WORD: Record<Category, string> = {
  metal: "Metal",
  gem: "Stone",
  ceramic: "Ceramic",
  pearl: "Pearl",
  other: "Material",
};

/** The viewer's finish ids, in a jeweller's words and in bench order.
 *  ⚠️ The brushed finish's id is "brushed" (`METAL_FINISHES` in the viewer's
 *  materials/metalPresets.ts); this table said "brush", so the tile was
 *  filtered out of the row and Brushed never showed. */
const FINISH_WORDS: Record<string, string> = {
  polished: "Polished",
  brushed: "Brushed",
  linear: "Line brushed",
  hammered: "Hammered",
  sand: "Sandblast",
  ice: "Ice",
  nature: "Nature",
};
const FINISH_ORDER = ["polished", "brushed", "linear", "hammered", "sand", "ice", "nature"];

/** Swatch art for a finish tile, drawn in the metal's own colour. */
function finishArt(id: string, c: number): string {
  const r = (c >> 16) & 255, g = (c >> 8) & 255, b = c & 255;
  const k = (m: number) => `rgb(${Math.round(Math.min(255, r * m))},${Math.round(Math.min(255, g * m))},${Math.round(Math.min(255, b * m))})`;
  switch (id) {
    case "polished":
      return `linear-gradient(115deg, ${k(0.62)} 0, ${k(1.12)} 38%, #fffaf0 46%, ${k(0.95)} 60%, ${k(0.66)} 100%)`;
    case "brushed":
      return `repeating-linear-gradient(90deg, ${k(1.02)} 0 1px, ${k(0.9)} 1px 2px, ${k(1.08)} 2px 3px), linear-gradient(${k(1)}, ${k(0.8)})`;
    case "linear":
      return `repeating-linear-gradient(0deg, ${k(1.04)} 0 1px, ${k(0.88)} 1px 3px), linear-gradient(90deg, ${k(0.8)}, ${k(1.05)})`;
    case "hammered":
      return `radial-gradient(circle at 20% 30%, ${k(1.3)} 0 6%, transparent 18%), radial-gradient(circle at 70% 25%, ${k(1.28)} 0 5%, transparent 17%), radial-gradient(circle at 45% 75%, ${k(1.25)} 0 6%, transparent 19%), radial-gradient(circle at 85% 80%, ${k(1.2)} 0 5%, transparent 16%), ${k(0.88)}`;
    case "sand":
      return `radial-gradient(${k(1.1)} 1px, transparent 1.2px) 0 0/3px 3px, radial-gradient(${k(0.82)} 1px, transparent 1.2px) 1px 1px/3px 3px, ${k(0.95)}`;
    case "ice":
      return `conic-gradient(from 30deg, ${k(1.25)}, ${k(0.8)}, ${k(1.3)}, ${k(0.85)}, ${k(1.25)})`;
    default:
      return `linear-gradient(135deg, ${k(1.1)}, ${k(0.75)} 60%, ${k(1)})`;
  }
}

/** Which named metal a colour is (exact to the catalogue, within rounding). */
function metalIdOf(hex: unknown, presets: { id: string; color: number }[]): string | null {
  if (typeof hex !== "string") return null;
  const n = parseInt(hex.replace("#", ""), 16);
  if (!Number.isFinite(n)) return null;
  const hit = presets.find((p) => {
    const d = Math.hypot(((n >> 16) & 255) - ((p.color >> 16) & 255), ((n >> 8) & 255) - ((p.color >> 8) & 255), (n & 255) - (p.color & 255));
    return d <= 6;
  });
  return hit?.id ?? null;
}

/** A named metal mark (Yellow Gold · Hammered), as opposed to a file or a ceramic. */
const isNamedMetal = (m: PresetMark | undefined): m is PresetMark =>
  !!m && !m.url && (m.category === undefined || m.category === "metal");

/**
 * Undo of a named metal re-applies the nearest one, so the engine needs the
 * catalogue's colours — set by whichever material panel is open, since any of
 * them can apply over a part the engine has never seen.
 */
function useMetalColors(engine: BasicEngine) {
  const { catalogs } = useViewer();
  useEffect(() => {
    engine.setMetalColors(Object.fromEntries(catalogs.metalPresets.map((p) => [p.id, p.color])));
  }, [engine, catalogs.metalPresets]);
}

function PartChips({
  parts,
  pick,
  choose,
  allLabel,
  everyLabel,
}: {
  parts: GroupInfo[];
  pick: string;
  choose: (id: string) => void;
  allLabel: string;
  /** A second "all" chip for every part, when "All" means one kind only. */
  everyLabel?: string;
}) {
  if (parts.length === 0) return null;
  return (
    <Section label="Apply to">
      <div className="flex flex-wrap gap-1.5">
        <Chip on={pick === "all"} onClick={() => choose("all")} data-part-chip="all">
          {allLabel}
        </Chip>
        {everyLabel ? (
          <Chip on={pick === EVERY_PART} onClick={() => choose(EVERY_PART)} data-part-chip={EVERY_PART}>
            {everyLabel}
          </Chip>
        ) : null}
        {/* A part word first, so a part never reads as a material choice: a
            file that names its part "18K_Rose" shows "Part 1 · 18K Rose", not a
            bare "18K Rose" beside a Yellow Gold selection. */}
        {parts.map((p, i) => (
          <Chip key={p.id} on={pick === p.id} onClick={() => choose(p.id)} title={p.label} data-part-chip={p.id}>
            {p.kind === "metal" ? "Part" : "Stones"} {i + 1}
            <span className="font-normal text-ed-text-2"> · {partName(p, i, p.kind)}</span>
          </Chip>
        ))}
      </div>
    </Section>
  );
}

/**
 * One material panel: the part chips, optionally the metal↔stone fixer, the
 * library grid for `category`, then whatever the panel adds underneath.
 */
function LibraryPanel({
  category,
  partScope,
  allLabel,
  everyLabel,
  noParts,
  fixer = false,
  // The library's own families ("Hammered", "With inclusions"): a chip row
  // appears wherever the presets declare them.
  subGroups = true,
  hideIds,
  labels,
  finishId,
  activeFallback = null,
  children,
}: {
  category: Category;
  partScope: PartScope;
  allLabel: (parts: GroupInfo[]) => string;
  everyLabel?: string;
  noParts: string;
  fixer?: boolean;
  subGroups?: boolean;
  hideIds?: string[];
  labels?: Record<string, string>;
  /** Named metals keep the finish the part already has. */
  finishId?: string;
  /** The tile to ring when we never applied anything to the part — Metals
   *  recognises a named metal by its colour. */
  activeFallback?: string | null;
  children?: ReactNode;
}) {
  const engine = useBasicEngine();
  useMetalColors(engine);
  const groups = (useDomainState("groups") as GroupInfo[] | undefined) ?? [];
  const { parts, pick, choose, ids } = partScope;
  const first = ids[0];

  const apply = (p: LibMaterial) => {
    const mark: PresetMark = p.fileUrl
      ? { presetId: p.id, url: p.fileUrl, category: p.category }
      : p.category === "metal"
        ? { presetId: p.id, finishId: finishId ?? "polished", category: "metal" }
        : { presetId: p.id, category: p.category };
    return engine.applyLibraryMaterial(ids, mark, `${STEP_WORD[p.category]}: ${p.label}`, `library:${category}`);
  };

  return (
    <>
      {parts.length === 0 ? <div className="mb-5"><Note>{noParts}</Note></div> : null}
      <PartChips parts={parts} pick={pick} choose={choose} allLabel={allLabel(parts)} everyLabel={everyLabel} />
      {fixer ? <KindFixer groups={groups} defaultOpen={parts.length === 0} /> : null}
      <Section label={STEP_WORD[category]}>
        <MaterialCategoryGrid
          categories={[category]}
          subGroups={subGroups}
          hideIds={hideIds}
          labels={labels}
          target={{
            apply,
            hasTarget: ids.length > 0,
            activeId: first ? engine.presetFor(first)?.presetId ?? activeFallback : null,
            custom: true,
          }}
        />
      </Section>
      {parts.length > 0 ? children : null}
    </>
  );
}

// ─────────────────────────────────────────────────────────────── Metals

export function MetalsPanel() {
  const { catalogs } = useViewer();
  const engine = useBasicEngine();
  const partScope = usePartScope("metal");
  const { parts, pick, scope } = partScope;

  const first = pick === "all" ? parts[0]?.id : pick;
  const mark = first ? engine.presetFor(first) : undefined;
  const named = isNamedMetal(mark) ? mark : undefined;
  const currentMetal = named?.presetId ?? metalIdOf(engine.read("metal.color", scope), catalogs.metalPresets);
  // A part wearing a library file or a ceramic has no finish of ours to ring.
  const currentFinish = named ? named.finishId ?? "polished" : mark ? null : "polished";
  const metalColor = catalogs.metalPresets.find((p) => p.id === currentMetal)?.color ?? 0xe5b377;
  const finishes = FINISH_ORDER.filter((id) => catalogs.metalFinishes.some((f) => f.id === id));

  const setFinish = (finishId: string) =>
    run(
      engine.applyPreset(
        "metal",
        { presetId: currentMetal ?? catalogs.metalPresets[0]?.id ?? "yellowGold", finishId },
        `Finish: ${FINISH_WORDS[finishId] ?? finishId}`,
        scope,
        "metalFinish",
      ),
    );

  return (
    <div data-basic-metal>
      <LibraryPanel
        category="metal"
        partScope={partScope}
        allLabel={() => "All metal"}
        noParts="This piece has no metal parts. If a part should be metal, switch it below."
        fixer
        finishId={currentFinish ?? "polished"}
        activeFallback={currentMetal}
      >
        <Section label="Finish">
          <div className="grid grid-cols-3 gap-2">
            {finishes.map((id) => (
              <Tile
                key={id}
                label={FINISH_WORDS[id] ?? id}
                on={currentFinish === id}
                disabled={engine.busy}
                thumbStyle={{ background: finishArt(id, metalColor) }}
                onClick={() => setFinish(id)}
                data-metal-finish={id}
              />
            ))}
          </div>
        </Section>
        <p className="text-[12px] leading-[17px] text-ed-text-2">
          Polish and brightness are on the right, under <b className="font-semibold text-ed-text">Fine-tune</b>.
        </p>
      </LibraryPanel>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────── Gems

/**
 * `diamondClear2` is deliberately absent from Basic: measured side by side
 * with `diamondClear` it moves the render by 0.019 of a block-mean level
 * against a 0.026 bar — a choice a jeweller cannot see is only one more
 * decision. It stays in Advanced.
 */
const GEM_HIDDEN = ["diamondClear2"];
/** The viewer's stone names, as a jeweller says them. */
const GEM_WORDS: Record<string, string> = { diamondClear: "Diamond", diamondWarm: "Warm diamond" };

/** Tint names a jeweller would use for the stone-colour swatches. */
const TINT_WORDS: Record<string, string> = {
  white: "Colourless",
  champagne: "Champagne",
  yellow: "Canary",
  pink: "Pink",
  blue: "Blue",
  green: "Green",
};

export function GemsPanel() {
  const engine = useBasicEngine();
  const partScope = usePartScope("gem");
  const { scope } = partScope;
  const tint = BASIC_CONTROLS.find((c) => c.id === "stoneColour") as SwatchDef;
  const curColor = engine.read("gem.color", scope);
  const curHex = typeof curColor === "string" ? curColor.toLowerCase() : null;
  const tints = Array.isArray(tint?.options) ? tint.options : [];

  return (
    <div data-basic-stones>
      <LibraryPanel
        category="gem"
        partScope={partScope}
        allLabel={() => "All stones"}
        noParts="This piece has no stones the studio recognises. If a part should sparkle, mark it as a stone below."
        fixer
        hideIds={GEM_HIDDEN}
        labels={GEM_WORDS}
      >
        <Section label="Colour tint">
          <div className="grid grid-cols-3 gap-1.5">
            {tints.map((o) => (
              <Swatch
                key={o.id}
                label={TINT_WORDS[o.id] ?? o.label}
                fill={o.hex}
                on={curHex === o.hex.toLowerCase()}
                disabled={engine.busy}
                onClick={() => run(engine.setValues([{ key: "gem.color", value: o.hex }], `Stone colour: ${TINT_WORDS[o.id] ?? o.label}`, scope, "stoneColour"))}
                data-basic-swatch={o.id}
              />
            ))}
          </div>
        </Section>
        <p className="text-[12px] leading-[17px] text-ed-text-2">
          Sparkle, fire and brilliance are on the right, under <b className="font-semibold text-ed-text">Fine-tune</b>.
        </p>
      </LibraryPanel>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────── Ceramics · Pearls · Other

/** A ceramic is a surface for the metal parts — a black or white ceramic band. */
export function CeramicsPanel() {
  const partScope = usePartScope("metal");
  return (
    <div data-basic-ceramics>
      <LibraryPanel category="ceramic" partScope={partScope} allLabel={() => "All metal"} noParts="This piece has no metal parts to make ceramic." />
    </div>
  );
}

/**
 * Pearls go on the stones. A pearl is a PBR surface, so once one is applied
 * the viewer files that part as metal — the pool keeps every part already
 * wearing a pearl we applied, so a second pearl can replace the first.
 *
 * Deliberately NOT matched by name: a "Pearl_Cup" is the metal that holds the
 * pearl, and "All" would paint it.
 */
export function PearlsPanel() {
  const engine = useBasicEngine();
  const partScope = usePartScope("gem", (g) => engine.presetFor(g.id)?.category === "pearl");
  return (
    <div data-basic-pearls>
      <LibraryPanel
        category="pearl"
        partScope={partScope}
        allLabel={(parts) => (parts.every((p) => p.kind === "gem") ? "All stones" : "All pearls & stones")}
        noParts="This piece has no stones the studio recognises. If a part is a pearl, mark it as a stone under Gems first."
      >
        <p className="text-[12px] leading-[17px] text-ed-text-2">
          A pearl not listed? Mark it as a stone under <b className="font-semibold text-ed-text">Gems</b>, then pick it here.
        </p>
      </LibraryPanel>
    </div>
  );
}

/**
 * Everything else (enamels, carbon, coral, wood…) — any part may wear it.
 *
 * "All" is every METAL part. It used to be every part, and an enamel or a wood
 * is a solid surface the viewer files as metal, so one click on the default
 * turned the stones into metal too — the customer configurator then saved
 * them as metal. The stones are still a chip away ("All parts", or a stone).
 */
export function OtherMaterialsPanel() {
  const partScope = usePartScope("any", undefined, { allKind: "metal" });
  return (
    <div data-basic-other>
      <LibraryPanel
        category="other"
        partScope={partScope}
        allLabel={() => "All metal parts"}
        everyLabel="All parts"
        noParts="This piece has no parts yet."
        subGroups
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────── Is a part wrong?

/**
 * "Is a part wrong?" — the studio decides metal vs stone from the file, and
 * sometimes guesses wrong (a CZ named "Part 12", a setting named "Diamond").
 * One switch per part fixes it, through the same Undo as everything else.
 *
 * Folded by default under the part chips, so the presets stay the first thing
 * a panel shows; open by default when the panel has no parts to offer, since
 * then it is the only way forward.
 */
function KindFixer({ groups, defaultOpen }: { groups: GroupInfo[]; defaultOpen: boolean }) {
  const engine = useBasicEngine();
  const [open, setOpen] = useState(defaultOpen);
  useEffect(() => {
    if (defaultOpen) setOpen(true);
  }, [defaultOpen]);
  if (groups.length === 0) return null;
  const flip = (g: GroupInfo, kind: MaterialKind) => run(fixPartKind(engine, g, kind));
  return (
    <section className="mb-5">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="flex w-full items-center justify-between gap-2 rounded-lg py-0.5 text-left text-[12px] font-semibold leading-4 text-ed-text-2 hover:text-ed-text"
        data-kind-fixer
      >
        Is a part wrong?
        <Icon name={open ? "expand_less" : "expand_more"} size={18} />
      </button>
      {open ? (
        <div className="mt-2 divide-y divide-ed-border rounded-[10px] border border-ed-border">
          {groups.map((g, i) => (
            <div key={g.id} className="flex items-center gap-2 px-3 py-2" data-kind-row={g.id}>
              <span className="min-w-0 flex-1 truncate text-[13px] text-ed-text" title={g.label}>
                {partName(g, i, g.kind === "gem" ? "gem" : "metal")}
              </span>
              <div role="radiogroup" aria-label={`${g.label} is`} className="flex h-7 rounded-md bg-ed-bg p-[2px]">
                {(["metal", "gem"] as const).map((k) => (
                  <button
                    key={k}
                    role="radio"
                    aria-checked={g.kind === k}
                    disabled={engine.busy}
                    onClick={() => { if (g.kind !== k) flip(g, k); }}
                    className={`rounded px-2.5 text-[12px] font-medium ${g.kind === k ? "bg-ed-panel text-ed-text shadow-[0_1px_2px_rgba(0,0,0,0.1)]" : "text-ed-text-2 hover:text-ed-text"}`}
                  >
                    {k === "metal" ? "Metal" : "Stone"}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : null}
    </section>
  );
}
